import { css } from "lit";

export const styles = css`
  .streaming-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  /* Remove padding when chat is present for more message space */
  .streaming-content:has(live-chat) {
    padding: 0;
    gap: 0;
  }

  .streaming-header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  .header-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  .duration {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-02) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-secondary);
    font-variant-numeric: tabular-nums;
  }

  .video-container {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--sds-color-background-default-tertiary);
    border-radius: var(--sds-border-radius-03);
    overflow: hidden;
    min-height: 0;
  }

  .video-container stream-recorder {
    width: 100%;
    height: 100%;
  }

  /* Live content wrapper */
  .live-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 16px;
    min-height: 0;
    justify-content: center;
    align-items: center;
  }

  /* When chat is present, side-by-side layout */
  .live-content:has(live-chat) {
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;
    gap: 0;
  }

  /* Video in live mode - takes left half */
  .live-content .video-container {
    width: 50%;
    max-width: 50%;
    flex: 0 0 50%;
    align-self: stretch;
  }

  /* Chat - takes right half */
  .live-content live-chat {
    flex: 1;
    width: 50%;
    min-height: auto;
    height: auto;
    overflow: hidden;
  }

  live-chat {
    border-radius: var(--sds-border-radius-03);
    overflow: hidden;
  }

  .streaming-controls {
    display: flex;
    gap: 12px;
    justify-content: center;
    padding-top: 8px;
  }

  .error-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: var(--sds-color-background-danger-subtle);
    color: var(--sds-color-text-danger-default);
    border-radius: var(--sds-border-radius-03);
    font: var(--sds-typography-weight-regular) var(--sds-typography-scale-01) var(--sds-typography-family-body);
  }

  .preview-info-banner {
    display: flex;
    gap: var(--sds-size-space-300);
    padding: var(--sds-size-space-400);
    background: var(--sds-color-background-default-default);
    border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    border-radius: var(--sds-size-radius-400);
  }

  .preview-info-banner ph-info {
    flex-shrink: 0;
    color: var(--sds-color-icon-brand-default);
  }

  .preview-info-content {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-200);
  }

  .preview-info-content p {
    margin: 0;
    font-family: var(--sds-typography-family-body);
    font-size: var(--sds-typography-body-size-medium);
    font-weight: var(--sds-typography-weight-regular);
    color: var(--sds-color-text-default-default);
    line-height: var(--sds-typography-body-line-height-medium);
  }

  .preview-info-content p:first-child {
    font-weight: var(--sds-typography-weight-medium);
  }

  .preview-info-content ul {
    margin: 0;
    margin-left: var(--sds-size-space-600);
    padding-left: var(--sds-size-space-500);
    font-family: var(--sds-typography-family-body);
    font-size: var(--sds-typography-body-size-medium);
    color: var(--sds-color-text-default-default);
    line-height: var(--sds-typography-body-line-height-medium);
  }

  .preview-info-content li {
    margin: var(--sds-size-space-100) 0;
  }

  .connecting-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 40px;
    color: var(--sds-color-text-default-secondary);
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-02) var(--sds-typography-family-body);
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--sds-color-border-default-default);
    border-top-color: var(--sds-color-border-brand-default);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    .streaming-content {
      padding: 8px;
      gap: 12px;
      overflow: hidden auto;
    }

    .live-content {
      flex-direction: column;
      width: 100%;
    }

    .live-content .video-container {
      border-radius: 0;
      margin: 0 -8px;
      width: calc(100% + 16px);
      min-height: 40dvh;
      max-width: none;
      flex: 0 0 auto;
    }

    .video-container {
      border-radius: 0;
      margin: 0 -8px;
      width: calc(100% + 16px);
      min-height: 40dvh;
      flex: 0 0 auto;
    }

    live-chat {
      flex: 1;
      min-height: 150px;
      margin: 0 -8px;
      width: calc(100% + 16px);
      border-radius: 0;
    }

    .preview-info-banner {
      flex-shrink: 0;
      padding: var(--sds-size-space-200);
    }

    .preview-info-content {
      gap: var(--sds-size-space-100);
    }

    .preview-info-content ul {
      margin-left: var(--sds-size-space-400);
      padding-left: var(--sds-size-space-200);
    }

    .preview-info-content p,
    .preview-info-content li {
      font-size: var(--sds-typography-body-size-small);
      line-height: 1.4;
    }

    .streaming-controls {
      flex-direction: column;
      padding: 0 8px;
    }
  }

  /* Portrait mode: stack vertically with limited video height */
  @media (max-width: 768px) and (orientation: portrait) {
    .live-content:has(live-chat) {
      flex-direction: column;
    }

    .live-content .video-container {
      width: calc(100% + 16px);
      max-width: none;
      max-height: 30vh;
      min-height: auto;
      flex: 0 0 auto;
    }

    .live-content live-chat {
      width: calc(100% + 16px);
      flex: 1;
    }
  }

  /* Landscape mode: side-by-side layout with video on left, chat on right */
  @media (max-height: 500px) and (orientation: landscape) {
    .streaming-content {
      padding: 0;
      gap: 0;
      overflow: hidden;
    }

    .streaming-content:has(live-chat) {
      padding: 0;
      gap: 0;
    }

    .live-content {
      flex-direction: row;
      gap: 0;
      height: 100%;
    }

    .live-content:has(live-chat) {
      gap: 0;
      margin: 0;
      max-width: none;
    }

    .live-content .video-container {
      width: 50%;
      max-width: 50%;
      min-height: auto;
      height: 100%;
      margin: 0;
      border-radius: 0;
    }

    live-chat {
      flex: 1;
      width: 50%;
      min-height: auto;
      height: 100%;
      margin: 0;
      border-radius: 0;
    }

    .preview-info-banner {
      display: none;
    }

    .error-banner {
      padding: 8px 12px;
      font-size: 12px;
    }

    .streaming-controls {
      padding: 4px 8px;
      gap: 8px;
      flex-direction: row;
    }
  }

  .confirmation-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 0;
  }

  .confirmation-content p {
    margin: 0;
    font: var(--sds-typography-weight-regular) var(--sds-typography-scale-02) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-secondary);
  }

  .confirmation-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;
